/**
 * Классификация ошибок ввода.
 *
 * Одного выравнивания мало: нужно понять природу ошибки. Промах по соседней
 * клавише, неверный регистр и «печатал не в той раскладке» требуют разной
 * реакции обучающего алгоритма (ТЗ §7.7).
 */

import type { ErrorKind, TypingError } from '@type2x/shared-contracts';
import {
  areNeighbourCharacters,
  layoutForCharacter,
  strokeForCharacter,
  supportsCharacter,
  type KeyboardLayout,
} from '@type2x/keyboard-layouts';

import { align, type AlignmentStep } from './alignment.js';

/** Задержка перед нажатием, если она известна. */
export type HesitationLookup = (expectedIndex: number) => number;

const NO_HESITATION: HesitationLookup = () => 0;

/** Отличаются ли символы только регистром. */
function differsOnlyByCase(expected: string, actual: string): boolean {
  return expected !== actual && expected.toLowerCase() === actual.toLowerCase();
}

/**
 * Похоже ли на набор в чужой раскладке: ожидаемый символ раскладке
 * принадлежит, а введённый — нет.
 */
function looksLikeWrongLayout(layout: KeyboardLayout, expected: string, actual: string): boolean {
  return supportsCharacter(layout, expected) && !supportsCharacter(layout, actual);
}

/**
 * Классифицирует замену символа.
 *
 * Сравнение идёт в раскладке ожидаемого символа, а не сессии: в упражнении
 * с русскими и латинскими фрагментами раскладка сессии описывает только
 * половину текста, и «печатал не в той раскладке» во второй половине
 * выглядело бы обычной заменой. Для одноязычного текста ничего не меняется:
 * ожидаемый символ там всегда принадлежит раскладке сессии.
 */
function classifySubstitution(
  sessionLayout: KeyboardLayout,
  expected: string,
  actual: string,
): ErrorKind {
  if (differsOnlyByCase(expected, actual)) return 'case';

  const layout = layoutForCharacter(expected, sessionLayout);
  if (looksLikeWrongLayout(layout, expected, actual)) return 'layout';
  if (areNeighbourCharacters(layout, expected, actual)) return 'adjacent-key';
  return 'substitution';
}

function kindForStep(layout: KeyboardLayout, step: AlignmentStep): ErrorKind | null {
  switch (step.operation) {
    case 'match':
      return null;
    case 'delete':
      return 'omission';
    case 'insert':
      return 'insertion';
    case 'transpose':
      return 'transposition';
    case 'substitute':
      return classifySubstitution(layout, step.expected, step.actual);
  }
}

/**
 * Классифицирует ошибки одного фрагмента.
 *
 * @param layout раскладка, в которой велась печать;
 * @param expectedText ожидаемый текст;
 * @param actualText фактически введённый текст;
 * @param hesitation задержка перед символом ожидаемого текста, мс.
 */
export function classifyErrors(
  layout: KeyboardLayout,
  expectedText: string,
  actualText: string,
  hesitation: HesitationLookup = NO_HESITATION,
): TypingError[] {
  const errors: TypingError[] = [];

  for (const step of align(expectedText, actualText)) {
    const kind = kindForStep(layout, step);
    if (kind === null) continue;

    errors.push({
      kind,
      expected: step.expected,
      actual: step.actual,
      position: step.expectedIndex,
      hesitationMs: hesitation(step.expectedIndex),
    });
  }

  return errors;
}

/**
 * Классифицирует одно нажатие, когда позиция заведомо известна.
 * Быстрый путь для обратной связи в реальном времени: выравнивание здесь
 * не нужно и обошлось бы дороже (NFR-P-02).
 */
export function classifyKeystroke(
  layout: KeyboardLayout,
  expected: string,
  actual: string,
): ErrorKind | null {
  if (expected === actual) return null;
  return classifySubstitution(layout, expected, actual);
}

/** Требуется ли Shift для ввода символа в этой раскладке. */
export function requiresShift(layout: KeyboardLayout, character: string): boolean {
  return strokeForCharacter(layout, character)?.requiresShift ?? false;
}
